import { Component, Input, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatSelectionListChange } from "@angular/material/list";
import {
  ActionNetworkEvent,
  GetActionNetworkEventGQL,
  GetActionNetworkEventsGQL,
  GetAnCampaignIntegrationGQL,
} from "generated/graphql";
import { ActivatedRoute } from "@angular/router";
import { map, takeUntil } from "rxjs/operators";
import { environment } from "environments/environment";
import { WithDisposableSubject } from "core/mixins/with-disposable-subject";
import { EventFormState } from "app/events/helpers/event-form-state";

@Component({
  selector: "app-an-event-sync-options",
  templateUrl: "./an-event-sync-options.component.html",
})
export class AnEventSyncOptionsComponent
  extends WithDisposableSubject()
  implements OnInit {
  @Input() calendarEvent: EventFormState = null;

  campaignId: string;
  anEventSyncEnabled = false;
  anGroupId?: string;
  enableEventSync = false;
  eventSyncResultsLoading = false;
  selectedANEvent: any | null = null;
  anEvents: any[] = [];
  filteredAnEvents: any[] = [];

  searchInput = new FormControl<string>("");

  constructor(
    private _getANCampaignIntegrationGQL: GetAnCampaignIntegrationGQL,
    private _getANEventsGQL: GetActionNetworkEventsGQL,
    private _getANEventGQL: GetActionNetworkEventGQL,
    private _route: ActivatedRoute
  ) {
    super();
    this.campaignId = this._route.snapshot.queryParams.campaignId;
  }

  ngOnInit() {
    // Get AN integration info
    this._getANCampaignIntegrationGQL
      .fetch({ campaignId: this.campaignId })
      .pipe(map((r) => r.data.getCampaign.campaignIntegrations?.actionNetwork))
      .subscribe((anIntegration) => {
        this.anEventSyncEnabled = anIntegration?.eventSyncEnabled ?? false;
        this.anGroupId = anIntegration?.group?.groupId ?? null;
      });

    // Initialize with existing sync event from GraphQL data (for Edit dialog)
    if (this.calendarEvent && this.calendarEvent.actionNetworkEventId) {
      this._getANEventGQL
        .fetch(
          {
            calendarEventId: this.calendarEvent.id,
          },
          { fetchPolicy: "network-only" }
        )
        .pipe(map((r) => r.data.getActionNetworkEvent))
        .subscribe((anEvent) => {
          this.selectedANEvent = {
            ...anEvent,
            startDate: anEvent.startDate.replace('Z', ''),
          };
          this.enableEventSync = true;
        });
    }

    // Watch for search input changes
    this.searchInput.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe((text) => {
        const lowerCaseSearchTerm = text.toLowerCase();
        this.filteredAnEvents = this.anEvents.filter(
          (anEvent: ActionNetworkEvent) =>
            anEvent.administrativeTitle
              .toLowerCase()
              .includes(lowerCaseSearchTerm) ||
            anEvent.title.toLowerCase().includes(lowerCaseSearchTerm)
        );
      });
  }

  /**
   * Getter for parent components to check if event sync is enabled
   * Used by Add dialog to disable "Create multiple events" button
   */
  get isSyncEnabled(): boolean {
    return this.enableEventSync;
  }

  /**
   * Get the selected sync event data
   * Parent components can call this to get the sync event when creating/updating events
   * Returns the selected event object, or null if none selected
   */
  getSelectedANEvent(): ActionNetworkEvent {
    return this.selectedANEvent;
  }

  /**
   * Load Action Network events for selection
   */
  loadANEvents() {
    // Ensure events are only loaded once
    if (this.anEvents.length > 0) return;

    this.eventSyncResultsLoading = true;
    this._getANEventsGQL
      .fetch({ groupId: this.anGroupId })
      .pipe(map((r) => r.data.getActionNetworkEvents.nodes))
      .subscribe((events) => {
        this.anEvents = this.filteredAnEvents = events.map((event) => ({
          ...event,
          startDate: event.startDate.replace('Z', ''),
        }));
        this.eventSyncResultsLoading = false;
      });
  }

  /**
   * Handle enable/disable toggle for event sync
   */
  toggleEventSync(event: MatSlideToggleChange) {
    if (event.checked) {
      this.enableEventSync = true;
      // When sync is enabled, load events if none are selected
      // This handles both initial enable and re-enable after unsync
      if (!this.selectedANEvent) {
        this.loadANEvents();
      }
    } else {
      this.eventSyncResultsLoading = false;
      this.enableEventSync = false;
      this.selectedANEvent = null;
    }
  }

  /**
   * Handle event selection from the list
   */
  onSyncEventSelected(event: MatSelectionListChange) {
    this.selectedANEvent = event.options[0].value;
  }

  /**
   * Handle sync button click on an event
   */
  onEventSyncButtonClicked(eventItem: ActionNetworkEvent) {
    this.selectedANEvent = eventItem;
  }

  /**
   * Handle unsync button click
   * When an event is unsynced, show the event list again (for Edit dialog scenario
   * where user wants to select a different event to sync with)
   */
  unsyncANEvent() {
    this.selectedANEvent = null;
    this.loadANEvents();
  }

  /**
   * Handle clicking event name permalink (opens in new tab)
   */
  onEventLinkClick(e, permalink: string) {
    e.stopImmediatePropagation();
    window.open(
      `https://${
        environment.production ? "" : "dev."
      }actionnetwork.org/events/${permalink}`
    );
  }
}
